import { useState } from 'react';
import { newId, type GalleryImage, type GalleryModule, type MediaAsset } from '@inboundhost/shared';
import { imagePreviewUrl } from '../../lib/images';
import { MediaPicker } from './MediaPicker';

interface Props {
  value: GalleryModule | null;
  onSave: (v: GalleryModule) => void;
  siteId: string;
}

export function GalleryEditor({ value, onSave, siteId }: Props) {
  const [images, setImages] = useState<GalleryImage[]>(value?.images ?? []);

  function addAsset(asset: MediaAsset) {
    setImages((prev) => [...prev, { id: newId('img'), key: asset.key, alt: asset.filename }]);
  }

  function update(i: number, patch: Partial<GalleryImage>) {
    setImages(images.map((img, idx) => (idx === i ? { ...img, ...patch } : img)));
  }

  function remove(i: number) {
    setImages(images.filter((_, idx) => idx !== i));
  }

  return (
    <div className="bg-white rounded shadow p-6 space-y-6">
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <div key={img.id} className="border rounded p-2">
              <img
                src={imagePreviewUrl(img.key, { width: 300, height: 200, fit: 'cover' })}
                alt={img.alt ?? ''}
                className="w-full h-32 object-cover rounded"
              />
              <input
                className="mt-2 w-full text-sm border rounded px-2 py-1"
                placeholder="Caption"
                value={img.caption ?? ''}
                onChange={(e) => update(i, { caption: e.target.value })}
              />
              <button type="button" className="text-red-600 text-sm mt-1" onClick={() => remove(i)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <MediaPicker siteId={siteId} onSelect={addAsset} />

      <div>
        <button
          type="button"
          onClick={() => onSave({ images })}
          className="bg-gray-900 text-white px-4 py-1 rounded hover:bg-gray-800"
        >
          Save gallery
        </button>
      </div>
    </div>
  );
}
